import React, { useId } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import dayjs from "dayjs";
import { useChartTokens } from "../../theme/chartTokens";
import ChartTooltip from "./ChartTooltip";

/**
 * Hours logged over time. One series, so there is no legend — the card title
 * names it. Crosshair + tooltip on hover, per the interaction baseline.
 *
 * `data`: [{ date: "YYYY-MM-DD", hours: number }] in ascending date order.
 */
export default function HoursTrendChart({ data, height = 260 }) {
  const t = useChartTokens();
  const gradientId = useId();

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={t.seriesFillFrom} />
            <stop offset="100%" stopColor={t.seriesFillTo} />
          </linearGradient>
        </defs>

        {/* Horizontal hairlines only — vertical ones add noise without aiding reading. */}
        <CartesianGrid stroke={t.grid} vertical={false} />

        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={{ stroke: t.axis }}
          tick={{ fill: t.axisInk, fontSize: 11 }}
          tickMargin={8}
          minTickGap={24}
          tickFormatter={(value) => dayjs(value).format("D MMM")}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tick={{ fill: t.axisInk, fontSize: 11 }}
          width={44}
          allowDecimals={false}
        />

        <Tooltip
          cursor={{ stroke: t.axisInk, strokeWidth: 1, strokeDasharray: "3 3" }}
          content={
            <ChartTooltip
              labelFormatter={(value) => dayjs(value).format("ddd, D MMM YYYY")}
              formatValue={(value) => `${Number(value).toFixed(2)} h`}
            />
          }
        />

        <Area
          type="monotone"
          dataKey="hours"
          name="Hours"
          stroke={t.series}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          activeDot={{ r: 4, strokeWidth: 2, stroke: t.surface }}
          // The range toggle re-renders this often; replaying a draw-on
          // animation each time is noise, not delight.
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
